import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { CartItem, Product, User } from "../types";
import { apiRequest } from "../api";

type ShopContextValue = {
  cart: CartItem[];
  user: User | null;
  wishlist: string[];
  compare: string[];
  recentlyViewed: string[];
  addToCart: (product: Product, variant?: string) => void;
  toggleWishlist: (slug: string) => void;
  toggleCompare: (slug: string) => void;
  clearCompare: () => void;
  trackRecentlyViewed: (slug: string) => void;
  updateQuantity: (slug: string, quantity: number) => void;
  removeFromCart: (slug: string) => void;
  clearCart: () => void;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  subtotal: number;
};

const ShopContext = createContext<ShopContextValue | undefined>(undefined);

export function ShopProvider({ children }: { children: React.ReactNode }) {
  const [cart, setCart] = useState<CartItem[]>(() => JSON.parse(localStorage.getItem("grc-cart") || "[]"));
  const [user, setUser] = useState<User | null>(() => JSON.parse(localStorage.getItem("grc-user") || "null"));
  const [wishlist, setWishlist] = useState<string[]>(() => JSON.parse(localStorage.getItem("brs-wishlist") || "[]"));
  const [compare, setCompare] = useState<string[]>(() => JSON.parse(localStorage.getItem("brs-compare") || "[]"));
  const [recentlyViewed, setRecentlyViewed] = useState<string[]>(() => JSON.parse(localStorage.getItem("brs-recently-viewed") || "[]"));

  useEffect(() => localStorage.setItem("grc-cart", JSON.stringify(cart)), [cart]);
  useEffect(() => localStorage.setItem("grc-user", JSON.stringify(user)), [user]);
  useEffect(() => localStorage.setItem("brs-wishlist", JSON.stringify(wishlist)), [wishlist]);
  useEffect(() => localStorage.setItem("brs-compare", JSON.stringify(compare)), [compare]);
  useEffect(() => localStorage.setItem("brs-recently-viewed", JSON.stringify(recentlyViewed)), [recentlyViewed]);

  const addToCart = (product: Product, variant?: string) => {
    setCart((items) => {
      const existing = items.find((item) => item.product.slug === product.slug && item.variant === variant);
      if (existing) return items.map((item) => item === existing ? { ...item, quantity: item.quantity + 1 } : item);
      return [...items, { product, quantity: 1, variant }];
    });
  };
  const toggleWishlist = (slug: string) => setWishlist((items) => items.includes(slug) ? items.filter((item) => item !== slug) : [slug, ...items].slice(0, 40));
  const toggleCompare = (slug: string) => setCompare((items) => items.includes(slug) ? items.filter((item) => item !== slug) : [slug, ...items].slice(0, 4));
  const clearCompare = () => setCompare([]);
  const trackRecentlyViewed = (slug: string) => setRecentlyViewed((items) => [slug, ...items.filter((item) => item !== slug)].slice(0, 12));
  const updateQuantity = (slug: string, quantity: number) => setCart((items) => items.map((item) => item.product.slug === slug ? { ...item, quantity } : item).filter((item) => item.quantity > 0));
  const removeFromCart = (slug: string) => setCart((items) => items.filter((item) => item.product.slug !== slug));
  const clearCart = () => setCart([]);
  const login = async (email: string, password: string) => {
    const payload = await apiRequest<{ token: string; user: User }>("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
    localStorage.setItem("grc-token", payload.token);
    setUser(payload.user);
  };
  const register = async (name: string, email: string, password: string) => {
    const payload = await apiRequest<{ token: string; user: User }>("/auth/register", { method: "POST", body: JSON.stringify({ name, email, password }) });
    localStorage.setItem("grc-token", payload.token);
    setUser(payload.user);
  };
  const refreshUser = async () => {
    const payload = await apiRequest<User>("/auth/me");
    setUser(payload);
  };
  const logout = () => { localStorage.removeItem("grc-token"); setUser(null); };
  const subtotal = useMemo(() => cart.reduce((sum, item) => sum + (item.product.price?.amount || 0) * item.quantity, 0), [cart]);

  return <ShopContext.Provider value={{ cart, user, wishlist, compare, recentlyViewed, addToCart, toggleWishlist, toggleCompare, clearCompare, trackRecentlyViewed, updateQuantity, removeFromCart, clearCart, login, register, logout, refreshUser, subtotal }}>{children}</ShopContext.Provider>;
}

export function useShop() {
  const value = useContext(ShopContext);
  if (!value) throw new Error("useShop must be used inside ShopProvider");
  return value;
}